import { CellContext, ColumnDef, filterFns } from '@tanstack/react-table';

import { largeCell, mediumCell, smallCell, x2LargeCell, xSmallCell } from '@/constants/table';
import { Modify } from '@/model/common';
import { Instrument, InstrumentCurrenciesResponse, InstrumentResponse } from '@/types/sso';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { DateFormatter } from '@react-aria/i18n';
import { formatMarginRequirement } from './ohoHelper';

export interface InstrumentRow
    extends Modify<
        Instrument,
        {
            tableSymbol: string;
            tablePositionUTIPattern: string;
            tableTradeUTIPattern: string;
            tableCcy1Network: string;
            tableCcy1NetworkType: string;
            tableStartDate: string;
            tableEndDate: string;
            tableMarginRequirement: string;
        }
    > {}

const numberCell = (info: CellContext<InstrumentRow, any>) => {
    const num = info.getValue();
    if (num >= 0) return num;
    else return '-';
};

const commonColumns = (ccyMode: boolean, ccy2Mode: boolean, qtyMode: boolean, lang: UseLanguageReturn) => {
    return [
        {
            ...mediumCell,
            accessorKey: 'tableSymbol',
            header: lang.blotters.symbol,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue()
        },
        {
            ...largeCell,
            accessorKey: 'tradingSystemSymbol',
            header: lang.blotters.fixApiSymbol,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue()
        },

        {
            ...largeCell,
            accessorKey: 'description',
            header: lang.common.description,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...xSmallCell,
            accessorKey: 'currency1',
            header: ccyMode ? lang.blotters.ccy1 : lang.blotters.ccy,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...smallCell,
            accessorKey: 'underlying',
            header: lang.common.underlying,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...mediumCell,
            accessorKey: 'tableStartDate',
            header: lang.common.startDate,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...mediumCell,
            accessorKey: 'tableEndDate',
            header: lang.common.endDate,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...xSmallCell,
            accessorKey: 'currency2',
            header: ccy2Mode ? lang.blotters.ccy2 : lang.blotters.ccy,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...mediumCell,
            accessorKey: 'tableCcy1Network',
            header: lang.blotters.ccy1Network,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...largeCell,
            accessorKey: 'tableCcy1NetworkType',
            header: lang.blotters.ccy1NetworkType,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...largeCell,
            accessorKey: 'priceDecimals',
            header: lang.common.priceDecimals,
            cell: numberCell
        },
        {
            ...largeCell,
            accessorKey: 'minOrderSize',
            header: lang.common.minOrderSize,
            cell: numberCell
        },
        {
            ...x2LargeCell,
            accessorKey: 'tableMarginRequirement',
            header: lang.accountValue.marginUsed,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || 'N/A'
        },
        {
            ...mediumCell,
            accessorKey: 'quantityDecimals',
            header: qtyMode ? lang.blotters.quantity1Decimals : lang.blotters.quantityDecimals,
            cell: numberCell
        },
        {
            ...largeCell,
            accessorKey: 'quantity2Decimals',
            header: lang.blotters.quantity2Decimals,
            cell: numberCell
        },
        {
            ...largeCell,
            accessorKey: 'pipsDecimals',
            header: lang.blotters.pipsDecimals,
            cell: numberCell
        },
        {
            ...largeCell,
            accessorKey: 'swapDecimals',
            header: lang.blotters.swapDecimals,
            cell: numberCell
        },
        {
            ...x2LargeCell,
            accessorKey: 'settlementDays',
            header: lang.blotters.valueDateConvention,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue()
        },
        {
            ...mediumCell,
            accessorKey: 'cfi',
            header: lang.blotters.cfi,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...xSmallCell,
            accessorKey: 'upi',
            header: lang.blotters.upi,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-'
        },
        {
            ...mediumCell,
            filterFn: filterFns.equals,
            accessorKey: 'miFIDscope',
            header: lang.blotters.mifidScope,
            cell: (info: CellContext<InstrumentRow, any>) => {
                const miFIDscope = info.getValue();
                return <div className="flex items-center gap-2">{miFIDscope ? 'Yes' : 'No'}</div>;
            }
        },
        {
            accessorKey: 'tablePositionUTIPattern',
            header: lang.blotters.positionUtiPattern,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-',
            toolTip: `Position level UTI's are generated by concatenating the following elements:
· The LEI of the UTI generating entity
· The CFI for the instrument
· The symbol code for the instrument
· The counterpart id (this will be provided upon request)
· The date the position was opened`
        },
        {
            accessorKey: 'tableTradeUTIPattern',
            header: lang.blotters.tradeUtiPattern,
            cell: (info: CellContext<InstrumentRow, any>) => info.getValue() || '-',
            toolTip: `Trade level UTI's are generated by taking the LEI of the UTI generating entity, and concatenating it with the trade ExecId. In (the rare) case the ExecId takes the form of a guid / uuid, all '-' are removed from the guid and it is converted to upper case before concatenation to the LEI.`
        }
    ];
};

export const getCfdFuturesInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(false, false, false, lang).filter((column: any) => {
        return ![
            'currency1',
            'underlying',
            'quantity2Decimals',
            'pipsDecimals',
            'swapDecimals',
            'settlementDays',
            'tableCcy1Network',
            'tableCcy1NetworkType'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getCryptoCFDInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(false, false, false, lang).filter((column: any) => {
        return ![
            'currency1',
            'quantity2Decimals',
            'pipsDecimals',
            'swapDecimals',
            'settlementDays',
            'miFIDscope',
            'tableCcy1Network',
            'tableCcy1NetworkType',
            'tableStartDate',
            'tableEndDate'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getIndexCFDInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(false, false, false, lang).filter((column: any) => {
        return ![
            'tradingSystemSymbol',
            'currency1',
            'quantity2Decimals',
            'pipsDecimals',
            'swapDecimals',
            'settlementDays',
            'tableCcy1Network',
            'tableCcy1NetworkType',
            'tableStartDate',
            'tableEndDate'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getCommodityCFDInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(false, false, false, lang).filter((column: any) => {
        return ![
            'tradingSystemSymbol',
            'currency1',
            'quantity2Decimals',
            'pipsDecimals',
            'swapDecimals',
            'settlementDays',
            'tableCcy1Network',
            'tableCcy1NetworkType',
            'tableStartDate',
            'tableEndDate'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getRollingFXInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(true, true, true, lang).filter((column: any) => {
        return ![
            'tradingSystemSymbol',
            'underlying',
            'miFIDscope',
            'tableCcy1Network',
            'tableCcy1NetworkType',
            'tableStartDate',
            'tableEndDate'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getFXSpotInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(true, true, true, lang).filter((column: any) => {
        return ![
            'tablePositionUTIPattern',
            'tableTradeUTIPattern',
            'tradingSystemSymbol',
            'underlying',
            'cfi',
            'upi',
            'settlementDays',
            'miFIDscope',
            'tableCcy1Network',
            'tableCcy1NetworkType',
            'tableStartDate',
            'tableEndDate',
            'tableMarginRequirement'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const getCryptoSpotInstrumentColumns = (lang) => {
    const columns: ColumnDef<InstrumentRow>[] = commonColumns(true, true, true, lang).filter((column: any) => {
        return ![
            'tablePositionUTIPattern',
            'tableTradeUTIPattern',
            'underlying',
            'cfi',
            'upi',
            'settlementDays',
            'miFIDscope',
            'swapDecimals',
            'tableStartDate',
            'tableEndDate',
            'tableMarginRequirement'
        ].includes(column.accessorKey);
    });

    return columns;
};

export const convertToInstrumentRow = (
    instrument: InstrumentResponse,
    legalEntityId: string,
    entityId: number,
    instrumentCurrencies: InstrumentCurrenciesResponse[],
    dateFormatter: DateFormatter,
    numberFormatter: Intl.NumberFormat
): InstrumentRow => {
    let asset1 = '';
    let asset2 = '';
    let tradingCcy1 = '';
    let tradingCcy2 = '';
    let symbol = '';
    let networkCcy1 = '-';
    let networkCcy1Type = '-';

    if (instrument.type === 'CFD') {
        tradingCcy1 = instrument.underlying;
        tradingCcy2 = instrument.currency2;
        asset1 = instrument.assetName || instrument.underlying;
        asset2 = instrument.currency2;
        if (instrument.subType === 'Future') {
            symbol = instrument.symbol;
        } else {
            symbol = `${asset1}/${asset2}`;
        }
    } else {
        const [assetCcy1, assetCcy2] = instrument.assetName.split('/');
        tradingCcy1 = instrument.currency1;
        tradingCcy2 = instrument.currency2;
        asset1 = assetCcy1;
        asset2 = assetCcy2;
        symbol = instrument.assetName;
        if (instrument.subType === 'Spot') {
            const matchingCurrency = instrumentCurrencies.find(
                (currency) => currency.assetName === tradingCcy1 || currency.code === tradingCcy1
            );
            if (matchingCurrency && matchingCurrency?.networkMappings) {
                networkCcy1 = matchingCurrency?.networkMappings
                    ?.map((networkMapping) => networkMapping.network)
                    .join(', ');
                networkCcy1Type = matchingCurrency?.networkMappings
                    ?.map((networkMapping) => networkMapping.tokenType)
                    .join(', ');
            }
        }
    }

    return {
        ...instrument,
        currency1: asset1,
        currency2: asset2,
        underlying: asset1,
        tableSymbol: symbol,
        tablePositionUTIPattern: `${legalEntityId}${
            instrument?.cfi || ''
        }${tradingCcy1}${tradingCcy2}${entityId}[YYYYMMDD]`,
        tableTradeUTIPattern: `${legalEntityId}[ExecID]`,
        tableCcy1Network: networkCcy1,
        tableCcy1NetworkType: networkCcy1Type,
        tableEndDate: dateFormatter.format(new Date(instrument.endDate)) || '-',
        tableStartDate: dateFormatter.format(new Date(instrument.startDate)) || '-',
        tableMarginRequirement: formatMarginRequirement(instrument.marginRequirement, numberFormatter)
    };
};
